import { DragStartEvent, DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import { Case, LandTransporter } from '../types';

/**
 * ドラッグ操作の種類
 */
export enum DragOperationType {
  ASSIGN_TO_TRANSPORTER = 'assign_to_transporter',
  UNASSIGN_CASE = 'unassign_case',
  REASSIGN_CASE = 'reassign_case',
}

/**
 * ドロップ先の情報
 */
export interface DropTarget {
  type: 'transporter' | 'unassigned';
  landTransporter?: LandTransporter;
  transporterId?: string;
}

/**
 * ドラッグ&ドロップ操作の結果
 */
export interface DragDropResult {
  operationType: DragOperationType;
  case: Case;
  sourceTransporterId?: string | null;
  targetTransporterId?: string;
  updates: Partial<Case>;
}

/**
 * ドラッグ&ドロップの状態
 */
export interface DragDropState {
  activeCase: Case | null;
  dragOverTransporter: LandTransporter | null;
  canDrop: boolean;
  dropReason: string;
}

/**
 * ドラッグ&ドロップ操作を管理するサービスクラス
 * FL-55のリファクタリング: ドラッグ&ドロップロジックをUIから分離
 */
export class DragDropService {
  /**
   * ドラッグ開始イベントを処理する
   * @param event - ドラッグ開始イベント
   * @returns アクティブな案件、またはnull
   */
  static handleDragStart(event: DragStartEvent): Case | null {
    const { active } = event;
    const caseData = active.data.current?.case as Case;
    return caseData || null;
  }

  /**
   * ドラッグオーバーイベントを処理する
   * @param event - ドラッグオーバーイベント
   * @returns ドロップ先の情報、またはnull
   */
  static handleDragOver(event: DragOverEvent): DropTarget | null {
    const { over } = event;
    if (!over) return null;

    const dropData = over.data.current;
    if (!dropData) return null;

    if (dropData.type === 'transporter' && dropData.landTransporter) {
      return {
        type: 'transporter',
        landTransporter: dropData.landTransporter,
        transporterId: dropData.landTransporter.id,
      };
    }

    if (dropData.type === 'unassigned') {
      return {
        type: 'unassigned',
      };
    }

    return null;
  }

  /**
   * ドラッグ終了イベントを処理し、操作結果を生成する
   * @param event - ドラッグ終了イベント
   * @returns ドラッグ&ドロップ操作の結果、またはnull
   */
  static handleDragEnd(event: DragEndEvent): DragDropResult | null {
    const { active, over } = event;

    if (!over) return null;

    const caseData = active.data.current?.case as Case;
    const dropData = over.data.current;

    if (!caseData || !dropData) return null;

    const sourceTransporterId = caseData.assignedLandTransporterId;

    // 未割り当てエリアにドロップ
    if (dropData.type === 'unassigned') {
      return {
        operationType: DragOperationType.UNASSIGN_CASE,
        case: caseData,
        sourceTransporterId,
        updates: {
          assignedLandTransporterId: undefined,
          status: 'provisional' as const,
        },
      };
    }

    // 陸送員にドロップ
    if (dropData.type === 'transporter' && dropData.landTransporter) {
      const targetTransporterId = dropData.landTransporter.id;

      // 同じ陸送員への割り当ては無視
      if (sourceTransporterId === targetTransporterId) {
        return null;
      }

      const operationType = sourceTransporterId
        ? DragOperationType.REASSIGN_CASE
        : DragOperationType.ASSIGN_TO_TRANSPORTER;

      return {
        operationType,
        case: caseData,
        sourceTransporterId,
        targetTransporterId,
        updates: {
          assignedLandTransporterId: targetTransporterId,
          status: 'assigned' as const,
        },
      };
    }

    return null;
  }

  /**
   * ドラッグ&ドロップ操作の種類を判定する
   * @param case_ - 対象案件
   * @param dropTarget - ドロップ先
   * @returns 操作の種類
   */
  static determineOperationType(case_: Case, dropTarget: DropTarget): DragOperationType {
    if (dropTarget.type === 'unassigned') {
      return DragOperationType.UNASSIGN_CASE;
    }

    if (dropTarget.type === 'transporter') {
      return case_.assignedLandTransporterId
        ? DragOperationType.REASSIGN_CASE
        : DragOperationType.ASSIGN_TO_TRANSPORTER;
    }

    throw new Error(`不明なドロップ先のタイプ: ${dropTarget.type}`);
  }

  /**
   * ドラッグ&ドロップ操作が有効かどうかをチェックする
   * @param case_ - 対象案件
   * @param dropTarget - ドロップ先
   * @returns 有効な操作の場合true
   */
  static isValidOperation(case_: Case, dropTarget: DropTarget): boolean {
    // 未割り当てエリアへのドロップは常に有効
    if (dropTarget.type === 'unassigned') {
      return true;
    }

    // 同じ陸送員への割り当ては無効
    if (
      dropTarget.type === 'transporter' &&
      case_.assignedLandTransporterId === dropTarget.transporterId
    ) {
      return false;
    }

    return true;
  }

  /**
   * ドラッグ&ドロップ操作の説明文を生成する
   * @param result - ドラッグ&ドロップ操作の結果
   * @param getTransporterName - 陸送員名を取得する関数
   * @returns 操作の説明文
   */
  static generateOperationDescription(
    result: DragDropResult,
    getTransporterName: (id: string) => string
  ): string {
    const caseId = result.case.id;

    switch (result.operationType) {
      case DragOperationType.UNASSIGN_CASE: {
        const sourceName = result.sourceTransporterId
          ? getTransporterName(result.sourceTransporterId)
          : '未割り当て';
        return `案件 ${caseId} を ${sourceName} から未割り当てに変更`;
      }

      case DragOperationType.ASSIGN_TO_TRANSPORTER: {
        const targetName = result.targetTransporterId
          ? getTransporterName(result.targetTransporterId)
          : '不明';
        return `案件 ${caseId} を ${targetName} に割り当て`;
      }

      case DragOperationType.REASSIGN_CASE: {
        const fromName = result.sourceTransporterId
          ? getTransporterName(result.sourceTransporterId)
          : '未割り当て';
        const toName = result.targetTransporterId
          ? getTransporterName(result.targetTransporterId)
          : '不明';
        return `案件 ${caseId} を ${fromName} から ${toName} に再割り当て`;
      }

      default:
        return `案件 ${caseId} の操作`;
    }
  }

  /**
   * ドラッグ&ドロップ状態の初期値を取得する
   * @returns 初期状態
   */
  static getInitialState(): DragDropState {
    return {
      activeCase: null,
      dragOverTransporter: null,
      canDrop: true,
      dropReason: '',
    };
  }

  /**
   * ドラッグ&ドロップ状態をリセットする
   * @param currentState - 現在の状態
   * @returns リセットされた状態
   */
  static resetState(currentState: DragDropState): DragDropState {
    return {
      ...currentState,
      activeCase: null,
      dragOverTransporter: null,
      canDrop: true,
      dropReason: '',
    };
  }

  /**
   * ドラッグオーバー時の状態を更新する
   * @param currentState - 現在の状態
   * @param dropTarget - ドロップ先
   * @param canDrop - ドロップ可能かどうか
   * @param dropReason - ドロップできない理由（ある場合）
   * @returns 更新された状態
   */
  static updateDragOverState(
    currentState: DragDropState,
    dropTarget: DropTarget | null,
    canDrop: boolean,
    dropReason: string = ''
  ): DragDropState {
    return {
      ...currentState,
      dragOverTransporter: dropTarget?.landTransporter || null,
      canDrop,
      dropReason,
    };
  }
}
